import {OfflineScreen} from './routes/OfflineScreen';
import { ChildHealthScreen } from './routes/ChildHealthScreen';
import { ChildHealthRecordScreen } from './routes/ChildHealthRecordScreen';
import { ClinicPaymentsScreen } from './routes/ClinicPaymentsScreen';
import { PatientPaymentScreen } from './routes/PatientPaymentScreen';
// Top-level route table + auth gate. Anon → login only. Authed but no
// clinic membership → join-clinic. Authed + member → the app shell.

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { LoginScreen } from './routes/LoginScreen';
import { RegisterWorkspaceScreen } from './routes/RegisterWorkspaceScreen';
import { JoinClinicScreen } from './routes/JoinClinicScreen';
import { WorkListScreen } from './routes/WorkListScreen';
import { NewEncounterScreen } from './routes/NewEncounterScreen';
import { EncounterDetailScreen } from './routes/EncounterDetailScreen';
import { PharmacyQueueScreen } from './routes/PharmacyQueueScreen';
import { ProceduresQueueScreen } from './routes/ProceduresQueueScreen';
import { LabsScreen } from './routes/LabsScreen';
import { ClinicGate } from './routes/ClinicGate';
import { SetCredentialsScreen } from './routes/SetCredentialsScreen';
import { ActivityScreen } from './routes/ActivityScreen';
import { TeamScreen } from './routes/TeamScreen';
import { PatientRecordsScreen } from './routes/PatientRecordsScreen';
import { PatientRecordScreen } from './routes/PatientRecordScreen';
import { ShiftReportsScreen } from './routes/ShiftReportsScreen';
import { ReceptionScreen } from './routes/ReceptionScreen';
import { PersonalCardScreen } from './routes/PersonalCardScreen';
import { PatientRegistrationScreen } from './routes/PatientRegistrationScreen';
import { CardTypeRoute } from './routes/CardTypeRoute';
import { FamilyDetailsScreen } from './routes/FamilyDetailsScreen';
import { QueuesScreen } from './routes/QueuesScreen';
import { InboxScreen } from './routes/InboxScreen';
import { TriageScreen } from './routes/TriageScreen';
import { RoleLandingRedirect, RoleRoute } from './routes/RoleRoute';

export function App() {
    const { status, clinician } = useAuth();
    const location = useLocation();
    if (location.pathname.startsWith('/pay/')) return <Routes><Route path="/pay/:token" element={<PatientPaymentScreen />} /></Routes>;

    if (location.pathname === '/offline') return <Navigate to="/work-list" replace />;

    if (status === 'loading') {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-slate-500 text-sm">Loading…</div>
            </div>
        );
    }

    if (status === 'anon') {
        return (
            <Routes>
                <Route path="/login" element={<LoginScreen />} />
                <Route path="/register-workspace" element={<RegisterWorkspaceScreen />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    // authed but no username yet — this is an existing phone-OTP user who
    // just signed in. Force credential setup before letting them into the
    // clinic-scoped app so subsequent sessions can use the password lane.
    if (clinician && clinician.username == null) {
        return <SetCredentialsScreen />;
    }

    // authed — each destination is wrapped in a RoleRoute that redirects
    // off-role users to their landing tab. Sub-routes like /encounters/:id
    // are gated behind the same tab as their parent surface.
    return (
        <ClinicGate>
            <Routes>
                <Route
                    path="/reception"
                    element={<RoleRoute tab="reception"><ReceptionScreen /></RoleRoute>}
                />
                <Route
                    path="/reception/personal"
                    element={<RoleRoute tab="reception"><PersonalCardScreen /></RoleRoute>}
                />
                <Route
                    path="/reception/card/:cardType"
                    element={<RoleRoute tab="reception"><CardTypeRoute /></RoleRoute>}
                />
                <Route
                    path="/reception/family/:id"
                    element={<RoleRoute tab="reception"><FamilyDetailsScreen /></RoleRoute>}
                />
                <Route
                    path="/reception/patient/:id"
                    element={<RoleRoute tab="reception"><PatientRegistrationScreen /></RoleRoute>}
                />
                <Route
                    path="/queues"
                    element={<RoleRoute tab="queues"><QueuesScreen /></RoleRoute>}
                />
                <Route
                    path="/work-list"
                    element={<RoleRoute tab="work-list"><WorkListScreen /></RoleRoute>}
                />
                <Route
                    path="/encounters/new"
                    element={<RoleRoute tab="work-list"><NewEncounterScreen /></RoleRoute>}
                />
                <Route
                    path="/encounters/:id"
                    element={<RoleRoute tab="work-list"><EncounterDetailScreen /></RoleRoute>}
                />
                <Route
                    path="/encounters/:id/triage"
                    element={<RoleRoute tab="work-list"><TriageScreen /></RoleRoute>}
                />
                <Route
                    path="/pharmacy"
                    element={<RoleRoute tab="pharmacy"><PharmacyQueueScreen /></RoleRoute>}
                />
                <Route
                    path="/procedures"
                    element={<RoleRoute tab="procedures"><ProceduresQueueScreen /></RoleRoute>}
                />
                <Route
                    path="/labs"
                    element={<RoleRoute tab="labs"><LabsScreen /></RoleRoute>}
                />
                <Route path="/child-health" element={<RoleRoute tab="child-health"><ChildHealthScreen /></RoleRoute>} />
                <Route path="/child-health/:id" element={<RoleRoute tab="child-health"><ChildHealthRecordScreen /></RoleRoute>} />
                <Route path="/payments" element={<RoleRoute tab="payments"><ClinicPaymentsScreen /></RoleRoute>} />
                <Route
                    path="/team"
                    element={<RoleRoute tab="team"><TeamScreen /></RoleRoute>}
                />
                <Route
                    path="/patient-records"
                    element={<RoleRoute tab="patient-records"><PatientRecordsScreen /></RoleRoute>}
                />
                <Route
                    path="/patient-records/:id"
                    element={<RoleRoute tab="patient-records"><PatientRecordScreen /></RoleRoute>}
                />
                <Route
                    path="/shift-reports"
                    element={<RoleRoute tab="shift-reports"><ShiftReportsScreen /></RoleRoute>}
                />
                <Route
                    path="/activity"
                    element={<RoleRoute tab="activity"><ActivityScreen /></RoleRoute>}
                />
                {/* Inbox is unscoped by role — any clinic member can be
                    mentioned, so every role sees the same inbox surface. */}
                <Route path="/inbox" element={<InboxScreen />} />
                <Route path="/join-clinic" element={<JoinClinicScreen />} />
                <Route path="*" element={<RoleLandingRedirect />} />
            </Routes>
        </ClinicGate>
    );
}
